import HttpService from '@/services/HttpService';
import { BevestigingActiviteitRequestDto } from '@/domain/interface/activiteit/bevestigmelding/BevestigingActiviteitRequestDto';
import { ActiviteitDetailClass } from '@/domain/interface/activiteit/ActiviteitDetailClass';
import { Paginated } from '@/domain/interface/Paginated';
import { MeldingSearchParams } from '@/domain/interface/intern/MeldingSearchParams';
import { AantalTeValiderenDto } from '@/domain/interface/activiteit/AantalTeValiderenDto';
import { ToestelClass } from '@/domain/interface/activiteit/kenmerk/ToestelClass';
import { ActiviteitRequestDto } from '@/domain/interface/activiteit/ActiviteitRequestDto';
import { ToestelRequestDto } from '@/domain/interface/activiteit/kenmerk/ToestelRequestDto';
import { EindbeoordelingOnderhoudRequestDto } from '@/domain/interface/activiteit/eindbeoordeling/EindbeoordelingOnderhoudRequestDto';
import { EindbeoordelingKeuringRequestDto } from '@/domain/interface/activiteit/eindbeoordeling/EindbeoordelingKeuringRequestDto';
import { BevestigingActiviteitResponseDto } from '@/domain/interface/activiteit/bevestigmelding/BevestigingActiviteitResponseDto';
import { BevestigingActiviteitClass } from '@/domain/interface/activiteit/bevestigmelding/BevestigingActiviteitClass';
import { MeldingFilter } from '@/domain/interface/Filter';
import { FilterOptionsDto } from '@/domain/interface/FilterOptionsDto';

export default class ActiviteitService {
  private readonly PATH = '/bevestiging-melding/valideren-en-indienen';

  constructor(private readonly http: HttpService) {}

  valideerMeerdereMeldingen(meldingen: BevestigingActiviteitRequestDto[]): Promise<void> {
    return this.http.post<void>(`${this.PATH}`, meldingen);
  }

  activiteitenOphalen(url: string, searchParams: MeldingFilter): Promise<Paginated<ActiviteitDetailClass>> {
    return this.http.post<Paginated<ActiviteitDetailClass>>(url, {
      filter: searchParams.filter,
      sorting: searchParams.sorting,
    });
  }

  getFilterOptions(): Promise<FilterOptionsDto> {
    return this.http.get<FilterOptionsDto>(`/filter/options`);
  }

  teValiderenActiviteitenOphalen(url: string, searchParams: MeldingSearchParams): Promise<Paginated<ActiviteitDetailClass>> {
    return this.http.get<Paginated<ActiviteitDetailClass>>(url, {
      params: { ...searchParams },
    });
  }

  getAantalTeValiderenActiviteiten(url: string): Promise<AantalTeValiderenDto> {
    return this.http.get<AantalTeValiderenDto>(url);
  }

  async wijzigenIngediendeMelding(
    url: string,
    activiteit: ActiviteitDetailClass,
    toestel: ToestelClass,
    eindbeoordeling: EindbeoordelingKeuringRequestDto | EindbeoordelingOnderhoudRequestDto
  ): Promise<ActiviteitDetailClass> {
    const data = await this.http.post<ActiviteitDetailClass>(url, {
      activiteit: new ActiviteitRequestDto(activiteit),
      toestel: new ToestelRequestDto(toestel),
      eindbeoordeling,
    });
    if (data?.opmerkingen) {
      this.http.notificationService.showMultipleWarnings(data.opmerkingen);
    }
    return data;
  }

  validerenEnIndien(url: string, bevestigingActiviteitRequestDto: BevestigingActiviteitRequestDto): Promise<BevestigingActiviteitResponseDto> {
    return this.http.put<BevestigingActiviteitResponseDto>(url, bevestigingActiviteitRequestDto);
  }

  indienen(url: string, bevestigingActiviteitRequestDto: BevestigingActiviteitRequestDto): Promise<BevestigingActiviteitResponseDto> {
    return this.http.post<BevestigingActiviteitResponseDto>(url, bevestigingActiviteitRequestDto);
  }

  weigeren(url: string, bevestigingActiviteitRequestDto: BevestigingActiviteitRequestDto): Promise<BevestigingActiviteitResponseDto> {
    return this.http.put<BevestigingActiviteitResponseDto>(url, bevestigingActiviteitRequestDto);
  }

  async getBevestigingMelding(url: string): Promise<BevestigingActiviteitClass> {
    const bevestigingActiviteit = await this.http.get<BevestigingActiviteitClass>(url);
    return new BevestigingActiviteitClass(bevestigingActiviteit);
  }
}
